import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import { api } from "@/lib/api";
import { toast } from "sonner";
import { ArrowLeft, Printer, Pencil, Share2, Trash2, Mail, Phone, Globe, Building2 } from "lucide-react";
import MoveToFolderDialog from "@/components/MoveToFolderDialog";

const SCOPE_LABELS = {
  all_units: "Koskee kaikkia asuntoja",
  some_units: "Koskee osaa asunnoista",
  common_areas: "Koskee yleisiä tiloja",
  outdoor: "Koskee ulkoaluetta",
};

export default function NoticeView() {
  const { noticeId } = useParams();
  const navigate = useNavigate();
  const [n, setN] = useState(null);
  const [labels, setLabels] = useState({});
  const [moveOpen, setMoveOpen] = useState(false);

  useEffect(() => {
    api.get("/catalog/notice-templates").then(({ data }) => setLabels(data.impact_labels));
    api.get(`/notices/${noticeId}`).then(({ data }) => setN(data)).catch(() => {
      toast.error("Tiedotetta ei löytynyt");
      navigate("/app");
    });
  }, [noticeId, navigate]);

  const remove = async () => {
    if (!confirm("Poistetaanko tiedote pysyvästi?")) return;
    await api.delete(`/notices/${noticeId}`);
    toast.success("Tiedote poistettu");
    navigate("/app");
  };

  const copyShare = () => {
    if (!n?.share_token) return;
    const url = `${window.location.origin}/t/${n.share_token}`;
    navigator.clipboard.writeText(url);
    toast.success("Jakolinkki kopioitu");
  };

  if (!n) return <div className="min-h-screen flex items-center justify-center text-slate-500">Ladataan...</div>;

  return (
    <div className="min-h-screen bg-slate-100">
      <div className="print-hide">
        
        <div className="max-w-4xl mx-auto px-5 md:px-8 pt-6 flex items-center justify-between flex-wrap gap-2">
          <button onClick={() => navigate("/app")} className="text-sm text-slate-500 hover:text-slate-900 inline-flex items-center gap-1" data-testid="notice-view-back">
            <ArrowLeft className="h-4 w-4" /> Takaisin
          </button>
          <div className="flex items-center gap-2 flex-wrap">
            <button onClick={() => navigate(`/app/asukastiedote/${noticeId}/muokkaa`)} className="btn-ghost text-sm" data-testid="notice-view-edit">
              <Pencil className="h-4 w-4" /> Muokkaa
            </button>
            <button onClick={() => setMoveOpen(true)} className="btn-ghost text-sm" data-testid="notice-view-move-folder">
              <Building2 className="h-4 w-4" /> Siirrä kohteeseen
            </button>
            <button onClick={remove} className="btn-ghost text-sm" data-testid="notice-view-delete">
              <Trash2 className="h-4 w-4" /> Poista
            </button>
            <button onClick={() => window.print()} className="btn-primary text-sm" data-testid="notice-view-print">
              <Printer className="h-4 w-4" /> Tulosta / Tallenna PDF
            </button>
          </div>
        </div>
      </div>

      <div className="py-6 px-3 md:px-8">
        <NoticeBody n={n} labels={labels} />
      </div>

      {moveOpen && (
        <MoveToFolderDialog
          kind="notice"
          itemId={noticeId}
          currentPropertyId={n.folder_id}
          onClose={() => setMoveOpen(false)}
          onMoved={(newFolderId) => setN((cur) => ({ ...cur, folder_id: newFolderId }))}
        />
      )}
    </div>
  );
}

export function NoticeBody({ n, labels }) {
  const fmt = (d) => (d ? new Date(d).toLocaleDateString("fi-FI") : null);
  const startStr = fmt(n.date);
  const endStr = fmt(n.date_end);
  const backupStr = fmt(n.backup_date);
  // Date span: "1.4.2026 – 13.4.2026" jos molemmat; yksittäinen jos vain start
  const dateStr = startStr && endStr && startStr !== endStr
    ? `${startStr} – ${endStr}`
    : (startStr || endStr || null);
  const time = (n.time_start && n.time_end) ? `${n.time_start} – ${n.time_end}` : (n.time_start || n.time_end || null);

  return (
    <article className="notice-page notice-print" data-testid="notice-body">
      {/* HEADER — compact */}
      <header className="text-center pb-3 border-b border-slate-200">
        {n.company_logo ? (
          <img src={n.company_logo} alt="" className="h-14 max-w-[220px] mx-auto object-contain" />
        ) : null}
        <div className="mt-2 font-display text-lg font-semibold text-slate-900">{n.company_name || ""}</div>
        <div className="text-xs text-slate-600 mt-0.5 flex flex-wrap justify-center gap-x-3 gap-y-0.5">
          {n.company_phone && <span className="inline-flex items-center gap-1"><Phone className="h-3 w-3" />{n.company_phone}</span>}
          {n.company_email && <span className="inline-flex items-center gap-1"><Mail className="h-3 w-3" />{n.company_email}</span>}
          {n.company_website && <span className="inline-flex items-center gap-1"><Globe className="h-3 w-3" />{n.company_website}</span>}
        </div>
      </header>

      <div className="mt-4 mb-1 text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Tiedote asukkaille</div>
      <h1 className="font-display text-2xl font-semibold text-slate-900 leading-tight">{n.title}</h1>
      <div className="mt-0.5 text-sm text-slate-600">
        {n.site_name && <strong>{n.site_name}</strong>}
        {n.site_name && (n.site_address || n.site_city) && " · "}
        {n.site_address}{n.site_city ? `, ${n.site_city}` : ""}
        {n.site_section && <> · {n.site_section}</>}
      </div>
      {n.customer_name && (
        <div className="mt-1 text-sm text-slate-600" data-testid="notice-customer-line">
          Työn tilaaja: <span className="text-slate-800 font-semibold">{n.customer_name}</span>
        </div>
      )}

      <div className="mt-4">
        <p className="text-slate-800">Hyvä asukas,</p>

        {(n.work_summary || n.work_description) && (
          <Block title="Kuvaus">
            {n.work_summary && <p>{n.work_summary}</p>}
            {n.work_description && <p className="mt-0.5 whitespace-pre-line">{n.work_description}</p>}
            {n.scope && <p className="mt-0.5 text-xs text-slate-600">{SCOPE_LABELS[n.scope]}</p>}
          </Block>
        )}

        {(dateStr || time) && (
          <Block title="Työ suoritetaan">
            <p>
              {dateStr && <strong>{dateStr}</strong>}
              {dateStr && time && " "}
              {time && <span>klo {time}</span>}
            </p>
            {backupStr && <p className="text-xs text-slate-600 mt-0.5">Varapäivä: {backupStr}</p>}
          </Block>
        )}

        {n.impacts?.length > 0 && (
          <Block title="Vaikutukset">
            <ul className="list-disc ml-5 space-y-0.5">
              {n.impacts.map((k) => <li key={k}>{labels[k] || k}</li>)}
            </ul>
          </Block>
        )}

        {n.instructions?.length > 0 && (
          <Block title="Ohjeet asukkaille">
            <ul className="list-disc ml-5 space-y-0.5">
              {n.instructions.map((s, i) => <li key={i}>{s}</li>)}
            </ul>
          </Block>
        )}

        {n.extra_info && (
          <Block title="Lisätiedot">
            <p className="whitespace-pre-line">{n.extra_info}</p>
          </Block>
        )}
      </div>

      <footer className="mt-6 pt-3 border-t border-slate-200">
        <p className="text-slate-800">{n.signature_closing || "Ystävällisin terveisin"},</p>
        <p className="font-semibold text-slate-900 mt-0.5">{n.signed_by}</p>
        <p className="text-slate-700">{n.signed_company || n.company_name}</p>

        {(n.customer_contact || n.customer_contact_info) && (
          <div className="mt-3 text-xs text-slate-600">
            <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-0.5">Tilaajan yhteystiedot</div>
            {n.customer_contact && <div>{n.customer_contact}{n.customer_contact_info ? ` · ${n.customer_contact_info}` : ""}</div>}
            {!n.customer_contact && n.customer_contact_info && <div>{n.customer_contact_info}</div>}
          </div>
        )}

        {(n.company_phone || n.company_email) && (
          <div className="mt-3 text-xs text-slate-600">
            <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-0.5">Yhteystiedot</div>
            <div>
              {n.company_phone && <span>Puhelin: {n.company_phone}</span>}
              {n.company_phone && n.company_email && <span> · </span>}
              {n.company_email && <span>Sähköposti: {n.company_email}</span>}
            </div>
          </div>
        )}

        <div className="mt-4 text-[9px] text-slate-400 text-right">
          Created with Veraso
        </div>
      </footer>
    </article>
  );
}

function Block({ title, children }) {
  return (
    <section className="notice-block mt-3">
      <h2 className="font-display text-sm font-semibold text-slate-900 mb-0.5">{title}</h2>
      <div className="text-slate-800 leading-snug">{children}</div>
    </section>
  );
}
